import { Component, OnInit, ViewChild } from '@angular/core';
import {
    MemberAdminSidenavComponent,
    MemberAdminSidenavInput, MemberAdminSidenavOutput
} from '../../memberships/shared/member-admin-sidenav/member-admin-sidenav.component';
import { PageHeaderComponent } from '../../shared/page-header/page-header.component';
import { DetailsCardAction, DetailsCardContainerComponent } from '../../shared/cards/details-card-container/details-card-container.component';
import { ViolationStatement } from '../../../../models/entities/violation-statement';
import { ViolationStatus } from '../../../../models/entities/violation-status';
import { BehaviorSubject, filter } from 'rxjs';
import { ModelTypeKeys } from '../../../../enums/model-type-keys.enum';
import { PropertyManagementCompanyService } from '../../../../services/property-management-company/property-management-company.service';
import { PropertyManagementCompanyDataService } from '../../../../services/property-management-company/property-manager-company-data.service';
import { OrganizationDataService } from '../../../../services/organization/organization-data.service';
import { CsvExportService } from '../../../../services/csv-export/csv-export.service';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { ScrollService } from '../../../../services/scroll.service';
import { SidenavContentService } from '../../shared/sidenav/sidenav-content/sidenav-content.service';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { GrowlerService } from '../../../../services/growler.service';
import { ViolationsService } from '../../../../services/violations/violations.service';
import { finalize, map, switchMap } from 'rxjs/operators';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { StatusChangeSidenavComponent, StatusChangeSidenavInput, StatusChangeSidenavOutput } from '../../shared/sidenav/status-change-sidenav/status-change-sidenav.component';
import { MatDialogSizes } from '../../../../enums/mat-dialog-sizes.enum';
import { FormControl } from '@angular/forms';
import { MatCardModule } from '@angular/material/card';
import { MatDividerModule } from '@angular/material/divider';
import { MatListModule } from '@angular/material/list';
import { CombineLinesListDirective, LoadingListDirective, StatusChipLineDirective } from '../../shared/directives/list.directive';
import { AppStatusChipComponent } from '../../shared/status-chip/status-chip.component';
import { LegfiDatePipeModule } from '../../../../pipes/format/legfi-date.module';
import { AttachmentsCardComponent } from '../../shared/cards/attachments-card/attachments-card.component';
import { CardWithTableContentDirective } from '../../shared/cards/card.directive';
import { CommentsCardComponent } from '../../shared/cards/comments-card/comments-card.component';
import { NotesCardComponent } from '../../shared/cards/notes-card/notes-card.component';
import { ViolationHistoryTableComponent } from '../violation-history/violation-history.component';
import { ConfirmActionV2DialogComponent, ConfirmActionV2DialogInput } from '../../shared/dialogs/confirm-action-dialog/confirm-action-dialog.component';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { RouterButtonValue } from '../../../../models/entities/router-button-value';
import { ViolationMailSidenavComponent, ViolationMailSidenavInput } from '../violation-mail-sidenav/violation-mail-sidenav.component';
import { AddressLinePipe } from '../../../../pipes/format/address-line.pipe';

@UntilDestroy()
@Component({
    selector: 'app-violation-details',
    templateUrl: './violation-details.component.html',
    standalone: true,
    imports: [
        DetailsCardContainerComponent,
        MatCardModule,
        MatDialogModule,
        PageHeaderComponent,
        MatDividerModule,
        MatListModule,
        LoadingListDirective,
        CombineLinesListDirective,
        AppStatusChipComponent,
        LegfiDatePipeModule,
        StatusChipLineDirective,
        AttachmentsCardComponent,
        CardWithTableContentDirective,
        CommentsCardComponent,
        NotesCardComponent,
        ViolationHistoryTableComponent,
        MatButtonModule,
        MatIconModule,
        RouterLink,
        AddressLinePipe,
    ],
})
export class ViolationDetailsComponent implements OnInit
{
    @ViewChild(ViolationHistoryTableComponent) historyTable: ViolationHistoryTableComponent;

    routerBackButton: RouterButtonValue;
    actions: DetailsCardAction[];
    lines = 1;
    loadingInitialData = true;

    pageTitle = 'Violation';
    violation: ViolationStatement;
    violationStatus: ViolationStatus;
    violationStatuses: ViolationStatus[];

    readonly loadingSubject$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    readonly modelType = ModelTypeKeys.VIOLATION;

    constructor(
            private _propertyManagementCompanyService: PropertyManagementCompanyService,
            private _propertyManagementCompanyData: PropertyManagementCompanyDataService,
            private _organizationDataService: OrganizationDataService,
            private _violationService: ViolationsService,
            private _csvExportService: CsvExportService,
            private _activatedRoute: ActivatedRoute,
            private _router: Router,
            private _scrollService: ScrollService,
            private _sidenav: SidenavContentService,
            private _dialog: MatDialog,
            private _growler: GrowlerService,
    ) {
    }

    get pmcId()  {
        return this._propertyManagementCompanyData.pmcId;
    }

    ngOnInit() {
        this._scrollService.scrollToTop();
        this.lines = this.getDetailActions().length;

        this.routerBackButton = {
            label: 'Back',
            route: `/pmc/${this.pmcId}/violations`,
            queryParams: this._activatedRoute.snapshot.queryParams,
            ariaLabel: 'Go back to violations',
        };

        this._activatedRoute.params.pipe(
                filter((params) => !!params.violationId),
                map((params) => params.violationId as number),
                switchMap((violationId) => this.refreshViolation(violationId)),
                switchMap(() => {
                    if (this.violation.title) {
                        this.pageTitle = this.violation.title;
                    }
                    return this._propertyManagementCompanyService.getPropertyManagementViolationStatuses(
                            { organizationId: this.violation.organizationId }
                    );
                }),
                map((res) => {
                    this.violationStatuses = res.data !== null ? res.data.filter((status) => {
                        return status.organizationId === this.violation.organizationId;
                    }) : [];
                }),
                map(() => {
                    this.violationStatus = this.violationStatuses?.find((status) => {
                        return status.id === this.violation.violationStatusId;
                    });
                }),
                untilDestroyed(this),
        ).subscribe({
            next: () => this.loadingInitialData = false,
            error: () => {
                this.loadingInitialData = false;
                this._growler.oops('Unable to load violations');
            },
        });
    }

    openStatusChange() {
        const data: StatusChangeSidenavInput = {
            modelType: 'violations',
            model: this.violation,
            statuses: this.violationStatuses.filter((s) => s.organizationId === this.violation.organizationId),
        };

        this._sidenav.open(StatusChangeSidenavComponent, {
            data,
        }).afterClosed().subscribe((result: StatusChangeSidenavOutput) => {
            if (result?.success) {
                this.violation.violationStatusId = result.status.id;
                this.violation.status = result.status.title;
                this.historyTable.refreshHistory();
            }
        });
    }

    private refreshViolation(violationId: number) {
        return this._propertyManagementCompanyService.getPropertyManagementViolation(violationId).pipe(
                map((res) => {
                    this.violation = res;
                    this.actions = this.getDetailActions();
                }),
        );
    }

    private toggleCompleteOrPending(isMarkComplete: boolean) {
        const emailOwnerControl = new FormControl<boolean>(false);
        const data: ConfirmActionV2DialogInput = {
            title: isMarkComplete ? 'Close violation?' : 'Re-open violation?',
            content: isMarkComplete ? 'Mark this violation as completed.' : 'Remove the completion date and move to open violations for review.',
            confirmButton: isMarkComplete ? 'Yes, close this violation' : 'Yes, re-open this violation',
            cancelButton: 'No',
            control: {
                control: emailOwnerControl,
                type: 'toggle',
                label: `Notify ${ this.violation?.unit?.ownersOneLine } (via email) the current violation status.`,
            },
        };
        this._dialog.open(ConfirmActionV2DialogComponent, {
            panelClass: ['v2-dialog', 'confirmation-layout'],
            width: MatDialogSizes.SM,
            autoFocus: 'dialog',
            data,
        }).afterClosed().subscribe({
            next: (res) => {
                if (res?.success) {
                    this.loadingSubject$.next(true);
                    this._violationService.toggleCompleteOrPending(
                            [this.violation.id],
                            isMarkComplete ? 1 : 0,
                            !!emailOwnerControl.value,
                            this.violation.organizationId,
                    ).pipe(
                            switchMap(() => this.refreshViolation(this.violation.id)),
                            finalize(() => this.loadingSubject$.next(false)),
                            untilDestroyed(this),
                    ).subscribe({
                        next: () => {
                            const message = 'Violation ' + (isMarkComplete ? 'marked as complete' : 're-opened');
                            this._growler.success('Success', message);
                        },
                        error: () => this._growler.oops('Unable to update violation'),
                    });
                }
            },
        });
    }

    private generateViolationPdf() {
        this.loadingSubject$.next(true);
        this._violationService.generateViolationPdf(this.violation.organizationId, this.violation.id).pipe(
                finalize(() => this.loadingSubject$.next(false)),
                untilDestroyed(this),
        ).subscribe({
            next: (res) => {
                const filename = `payhoa-violation-${this.violation.id}.pdf`;
                this._csvExportService.downloadFile(res, filename, 'application/pdf');
            },
            error: () => this._growler.oops('Unable to generate file for violation'),
        });
    }

    private editViolation() {
        this.loadingSubject$.next(true);

        // TODO: new edit view for violation, redirecting to old view for now
        const redirectTarget = `/app/violations/edit/${this.violation.id}`;
        this._organizationDataService.switchOrganization(this.violation.organizationId, {}, redirectTarget).pipe(
                finalize(() => this.loadingSubject$.next(false)),
                untilDestroyed(this),
        ).subscribe({
            next: ([url, isExternalRedirect]: [string, boolean]) => {
                if (isExternalRedirect) {
                    window.location.href = url;
                } else {
                    // noinspection JSIgnoredPromiseFromCall
                    this._router.navigateByUrl(url, {skipLocationChange: true});
                }
            },
            error: () => this._growler.oops('There was a problem switching current organization.'),
        });
    }

    private newCharge() {
        // TODO: mat-dialog or sidenav for charge editor
        // this.chargeEditor.newCharge([this.violation.unitId]);
    }

    private showNotificationMemberSelector() {
        const data: MemberAdminSidenavInput = {
            title: 'Send Notifications',
            required: true,
            requireEmail: true,
            orgId: this.violation.organizationId,
            unitId: this.violation.unitId,
            confirmButton: {
                '=0': 'Notify Recipients',
                '=1': 'Notify 1 Recipient',
                other: 'Notify # Recipients',
            },
            emptyStateLabel: 'No recipients so far...',
        };

        this._sidenav.open(MemberAdminSidenavComponent, {
            data,
            parentClass: 'selector-layout',
        }).afterClosed().subscribe((result: MemberAdminSidenavOutput) => {
            if (result?.success && result?.selectedMemberships.length > 0) {
                this.loadingSubject$.next(true);

                const requestBody = {
                    memberIds: result.selectedMemberships.map((m) => m.id),
                    organizationId: this.violation.organizationId,
                };
                this._violationService.notifyViolationStatement(this.violation.id, requestBody).pipe(
                    finalize(() => this.loadingSubject$.next(false)),
                    untilDestroyed(this),
                ).subscribe({
                    next: () => {
                        const count = result.selectedMemberships.length === 1 ? ' notification email' : (result.selectedMemberships.length + ' notification emails');
                        this._growler.success('Success', `Sent ${count}`);
                    },
                    error: () => this._growler.oops('Unable to send notifications'),
                });
            }
        });
    }

    private showViolationMailer() {
        const data: ViolationMailSidenavInput = {
            violations: [this.violation],
            organizationId: this.violation.organizationId,
        };
        this._sidenav.open(ViolationMailSidenavComponent, {
            data,
        });
    }

    private deleteViolation() {
        const data: ConfirmActionV2DialogInput = {
            title: `Delete violation?`,
            content: 'This action cannot be undone.',
            confirmButton: 'Yes, delete violation',
            cancelButton: 'No',
        };
        this._dialog.open(ConfirmActionV2DialogComponent, {
            panelClass: ['v2-dialog', 'confirmation-layout'],
            width: MatDialogSizes.SM,
            autoFocus: 'dialog',
            data,
        }).afterClosed().subscribe((res) => {
            if (res?.success) {
                this.loadingSubject$.next(true);
                this._violationService.deleteViolation([this.violation.id], this.violation.organizationId).pipe(
                        finalize(() => this.loadingSubject$.next(false)),
                        untilDestroyed(this),
                ).subscribe({
                    next: () => {
                        this._growler.success('Success', `Violation deleted`);
                        return this._router.navigate([this.routerBackButton.route], {queryParams: this.routerBackButton.queryParams});
                    },
                    error: () => this._growler.oops('Unable to delete violation'),
                });
            }
        });
    }

    private getDetailActions(): DetailsCardAction[] {
        return [
            this.violation?.completedTime ? {
                icon: 'refresh',
                title:  'Re-open',
                action: 'reopen',
                callback: () => this.toggleCompleteOrPending(false),
            } : {
                svg: 'v2:fi-br-check',
                title:  'Mark as Complete',
                action: 'close',
                callback: () => this.toggleCompleteOrPending(true),
            },
            {
                svg: 'v2:fi-br-download',
                title: 'Download PDF',
                action: 'pdf',
                callback: () => this.generateViolationPdf(),
            },
            {
                svg: 'v2:fi-br-edit',
                title: 'Edit',
                action: 'edit',
                callback: () => this.editViolation(),
            },
            /*{
                svg: 'v2:fi-br-dollar',
                title: 'Create Fine',
                description : 'Coming Soon',
                action: 'fine',
                callback: () => this.newCharge(),
            },*/
            {
                svg: 'v2:fi-br-paper-plane',
                title: 'Notify Others',
                action: 'email',
                callback: () => this.showNotificationMemberSelector(),
            },
            {
                svg: 'v2:fi-br-envelope',
                title: 'Send Mail',
                action: 'mail',
                callback: () => this.showViolationMailer(),
            },
            {
                svg: 'v2:fi-br-trash',
                title: 'Delete',
                action: 'delete',
                callback: () => this.deleteViolation(),
            },
        ];
    }
}
